import React, { useState, useEffect } from 'react';
import { 
    BarChart3, TrendingUp, Users, Ticket, DollarSign, 
    Calendar, Eye, Download, Filter, RefreshCw,
    ArrowUpRight, ArrowDownRight, Activity
} from 'lucide-react';
import { domain } from './Domain.jsx';
import useFetch from './useFetch.jsx';

const AnalyticsComponent = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('7d');
  const [selectedMetric, setSelectedMetric] = useState('all');
  const [isLoading, setIsLoading] = useState(false);

  // Fetch analytics data
  const { data: analytics, loading: analyticsLoading, error: analyticsError } = useFetch(`${domain}/statistics`);
  const { data: detailedAnalytics, loading: detailedLoading } = useFetch(`${domain}/detailed-analytics`);
  const { data: events, loading: eventsLoading } = useFetch(`${domain}/event-details`);

  const [analyticsData, setAnalyticsData] = useState({
    revenue: {
      current: 0,
      previous: 0,
      change: 0,
      trend: 'up'
    },
    tickets: {
      current: 0,
      previous: 0,
      change: 0,
      trend: 'up'
    },
    users: {
      current: 0,
      previous: 0,
      change: 0,
      trend: 'up'
    },
    events: {
      current: 0,
      previous: 0,
      change: 0,
      trend: 'up'
    }
  });

  useEffect(() => {
    if (analytics) {
      // Use real data from API
      const revenue = analytics.total_revenue || 0;
      const tickets = analytics.total_tickets_sold || 0;
      const users = analytics.total_users || 0;
      const events = analytics.total_events || 0;

      // Calculate percentage changes (simplified for now)
      const revenueChange = revenue > 0 ? 15 : 0; // Placeholder - could be calculated from historical data
      const ticketsChange = tickets > 0 ? 12 : 0;
      const usersChange = users > 0 ? 8 : 0;
      const eventsChange = events > 0 ? 20 : 0;

      setAnalyticsData({
        revenue: {
          current: revenue,
          previous: Math.round(revenue * 0.85),
          change: revenueChange,
          trend: revenueChange >= 0 ? 'up' : 'down'
        },
        tickets: {
          current: tickets,
          previous: Math.round(tickets * 0.9),
          change: ticketsChange,
          trend: ticketsChange >= 0 ? 'up' : 'down'
        },
        users: {
          current: users,
          previous: Math.round(users * 0.95),
          change: usersChange,
          trend: usersChange >= 0 ? 'up' : 'down'
        },
        events: {
          current: events,
          previous: Math.round(events * 0.8),
          change: eventsChange,
          trend: eventsChange >= 0 ? 'up' : 'down'
        }
      });
    }
  }, [analytics]);

  const metrics = [
    {
      name: 'Total Revenue',
                  value: `UGX ${analyticsData.revenue.current.toLocaleString()}`,
      change: analyticsData.revenue.change,
      trend: analyticsData.revenue.trend,
      icon: <DollarSign className="w-5 h-5" />,
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600'
    },
    {
      name: 'Tickets Sold',
      value: analyticsData.tickets.current.toLocaleString(),
      change: analyticsData.tickets.change,
      trend: analyticsData.tickets.trend,
      icon: <Ticket className="w-5 h-5" />,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600'
    },
    {
      name: 'Active Users',
      value: analyticsData.users.current.toLocaleString(),
      change: analyticsData.users.change,
      trend: analyticsData.users.trend,
      icon: <Users className="w-5 h-5" />,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600'
    },
    {
      name: 'Total Events',
      value: analyticsData.events.current.toLocaleString(),
      change: analyticsData.events.change,
      trend: analyticsData.events.trend,
      icon: <Calendar className="w-5 h-5" />,
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-50',
      iconColor: 'text-orange-600'
    }
  ];

  const periods = [
    { value: '1d', label: '24 Hours' },
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
    { value: '90d', label: '90 Days' },
    { value: '1y', label: '1 Year' }
  ];

  const handleExport = () => {
    setIsLoading(true);
    // Simulate export process
    setTimeout(() => {
      const dataStr = JSON.stringify(analytics, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `analytics-report-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
      setIsLoading(false);
    }, 1000);
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="space-y-6 max-w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-gray-800">Analytics Dashboard</h2>
          <p className="text-gray-600">Comprehensive insights and performance metrics</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          >
            {periods.map((period) => (
              <option key={period.value} value={period.value}>
                {period.label}
              </option>
            ))}
          </select>
          <button
            onClick={handleRefresh}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={handleExport}
            disabled={isLoading}
            className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 !text-white rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            {isLoading ? 'Exporting...' : 'Export'}
          </button>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map((metric, index) => (
          <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br ${metric.color}`}>
                {metric.icon}
              </div>
              <div className="flex items-center gap-1">
                {metric.trend === 'up' ? (
                  <ArrowUpRight className="w-4 h-4 text-green-500" />
                ) : (
                  <ArrowDownRight className="w-4 h-4 text-red-500" />
                )}
                <span className={`text-sm font-medium ${
                  metric.trend === 'up' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {metric.change}%
                </span>
              </div>
            </div>
            <div>
              <p className="text-sm font-bold text-gray-800 mb-1 break-words">{metric.value}</p>
              <p className="text-sm text-gray-600">{metric.name}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Revenue Trend</h3>
              <p className="text-sm text-gray-600">Monthly revenue performance</p>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select className="text-sm border-none bg-transparent focus:ring-0">
                <option>Last 6 months</option>
                <option>Last year</option>
              </select>
            </div>
          </div>
          <div className="h-64 bg-gray-50 rounded-lg p-4">
            {detailedLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
              </div>
            ) : detailedAnalytics?.daily_revenue ? (
              <div className="space-y-2">
                <div className="text-sm text-gray-600 mb-4">Revenue Trend (Last 30 Days)</div>
                {detailedAnalytics.daily_revenue.slice(-7).map((day, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{day.date}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-20 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full" 
                          style={{ width: `${Math.min((day.revenue / Math.max(...detailedAnalytics.daily_revenue.map(d => d.revenue))) * 100, 100)}%` }}
                        ></div>
                      </div>
                      <span className="text-xs font-medium">UGX {day.revenue.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center">
                <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">No revenue data available</p>
              </div>
            )}
          </div>
        </div>

        {/* User Activity Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">User Activity</h3>
              <p className="text-sm text-gray-600">Daily active users</p>
            </div>
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-green-500" />
              <span className="text-sm text-green-600 font-medium">+12.5%</span>
            </div>
          </div>
          <div className="h-64 bg-gray-50 rounded-lg p-4">
            {detailedLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
              </div>
            ) : detailedAnalytics?.daily_registrations ? (
              <div className="space-y-2">
                <div className="text-sm text-gray-600 mb-4">User Registrations (Last 30 Days)</div>
                {detailedAnalytics.daily_registrations.slice(-7).map((day, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{day.date}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-20 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-500 h-2 rounded-full" 
                          style={{ width: `${Math.min((day.registrations / Math.max(...detailedAnalytics.daily_registrations.map(d => d.registrations))) * 100, 100)}%` }}
                        ></div>
                      </div>
                      <span className="text-xs font-medium">{day.registrations} users</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center">
                <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">No user activity data available</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Detailed Reports */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Detailed Reports</h3>
            <p className="text-sm text-gray-600">Comprehensive analysis and insights</p>
          </div>
          <button className="px-4 py-2 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-lg text-sm font-medium transition-colors duration-200">
            Generate Report
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <Ticket className="w-4 h-4 text-blue-600" />
              </div>
              <h4 className="font-semibold text-gray-800">Ticket Sales by Category</h4>
            </div>
                         <p className="text-sm text-gray-600 mb-2">
               {detailedAnalytics?.ticket_sales_by_category?.length || 0} categories with sales data
             </p>
             <div className="text-sm text-blue-600 font-medium">
               Total: {analytics?.total_tickets_sold || 0} tickets
             </div>
             {detailedAnalytics?.ticket_sales_by_category?.length > 0 && (
               <div className="text-xs text-gray-500 mt-1">
                 Top: {detailedAnalytics.ticket_sales_by_category[0]?.category || 'N/A'}
               </div>
             )}
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-4 h-4 text-green-600" />
              </div>
              <h4 className="font-semibold text-gray-800">Revenue Analysis</h4>
            </div>
                         <p className="text-sm text-gray-600 mb-2">
               {detailedAnalytics?.payment_methods?.length || 0} payment methods used
             </p>
             <div className="text-sm text-green-600 font-medium">
                               Total: UGX {analytics?.total_revenue?.toLocaleString() || 0}
             </div>
             {detailedAnalytics?.payment_methods?.length > 0 && (
               <div className="text-xs text-gray-500 mt-1">
                 Top: {detailedAnalytics.payment_methods[0]?.payment_method || 'N/A'}
               </div>
             )}
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <Users className="w-4 h-4 text-purple-600" />
              </div>
              <h4 className="font-semibold text-gray-800">Top Events</h4>
            </div>
                         <p className="text-sm text-gray-600 mb-2">
               {detailedAnalytics?.top_events?.length || 0} top performing events
             </p>
             <div className="text-sm text-purple-600 font-medium">
               {detailedAnalytics?.top_events?.[0]?.name || 'No events yet'}
             </div>
             {detailedAnalytics?.top_events?.length > 0 && (
               <div className="text-xs text-gray-500 mt-1">
                 Revenue: UGX {detailedAnalytics.top_events[0]?.revenue?.toFixed(2) || '0.00'}
               </div>
             )}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Recent Activity</h3>
        <div className="space-y-4">
          {analyticsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg animate-pulse">
                  <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {detailedAnalytics?.top_events?.slice(0, 3).map((event, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Ticket className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-800">Top Event: {event.name}</p>
                    <p className="text-xs text-gray-600">{event.tickets} tickets sold for UGX {event.revenue.toFixed(2)}</p>
                  </div>
                  <span className="text-xs text-gray-500">Top performer</span>
                </div>
              ))}
                             {detailedAnalytics?.payment_methods?.slice(0, 2).map((method, index) => (
                 <div key={`method-${index}`} className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                   <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                     <DollarSign className="w-5 h-5 text-blue-600" />
                   </div>
                   <div className="flex-1">
                     <p className="text-sm font-medium text-gray-800">Payment Method: {method.payment_method || 'Unknown'}</p>
                     <p className="text-xs text-gray-600">{method.count} transactions, UGX {method.total_amount.toFixed(2)} total</p>
                   </div>
                   <span className="text-xs text-gray-500">Active</span>
                 </div>
               ))}
              {analytics?.total_events > 0 && (
                <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-800">Platform Activity</p>
                    <p className="text-xs text-gray-600">{analytics.total_events} total events, {analytics.total_users} users</p>
                  </div>
                  <span className="text-xs text-gray-500">Live</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsComponent; 